// /fiscal/invoices — Story 10.7 (E10).
// Lista de NFs emitidas + filtros + drawer detalhe + downloads.

import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, CalendarClock, Download, FileText, Filter, Receipt, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { addDaysLocal, formatBR, formatBRDateTime, todayLocal } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { PageHelp } from '../../components/common/PageHelp';
import { PageTabs, TabPanel, type PageTab } from '../../components/common/PageTabs';
import { CancelFiscalInvoiceModal } from '../../components/fiscal/CancelFiscalInvoiceModal';
import { StandaloneEmitModal } from '../../components/fiscal/StandaloneEmitModal';
import { triggerFiscalDownload, useFiscalInvoice, useFiscalInvoices } from '../../hooks/use-fiscal';
import { useTabParam } from '../../hooks/use-tab-param';
import type { FiscalInvoice, FiscalInvoiceStatus } from '../../lib/fiscal-api';

import { FiscalRecurringTab } from './recurring';

const STATUS_OPTIONS: { value: FiscalInvoiceStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'issued', label: 'Emitidas' },
  { value: 'pending', label: 'Pendentes' },
  { value: 'issuing', label: 'Em emissão' },
  { value: 'failed', label: 'Falhadas' },
  { value: 'pending_data', label: 'Dados pendentes' },
  { value: 'cancelled', label: 'Canceladas' },
  { value: 'discarded', label: 'Descartadas' },
];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  issuing: 'Em emissão',
  issued: 'Emitida',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  pending_data: 'Dados pendentes',
  discarded: 'Descartada',
};

const STATUS_TONE: Record<string, string> = {
  pending: 'border-info/40 bg-info/10 text-info',
  issuing: 'border-info/40 bg-info/10 text-info',
  issued: 'border-good/40 bg-good/10 text-good',
  failed: 'border-bad/40 bg-bad/10 text-bad',
  cancelled: 'border-line bg-panel-3 text-muted',
  pending_data: 'border-warn/40 bg-warn/10 text-warn',
  discarded: 'border-line bg-panel-3 text-muted-2',
};

const FISCAL_TABS: PageTab<'emitidas' | 'recorrentes'>[] = [
  { id: 'emitidas', label: 'Emitidas', icon: Receipt },
  { id: 'recorrentes', label: 'Recorrentes', icon: CalendarClock },
];

/**
 * Hub de Notas Fiscais — consolida Emitidas + Recorrentes sob abas na mesma
 * URL (?tab=). Cada aba mantém seus próprios filtros; trocar de aba desmonta a
 * outra (filtros resetam ao voltar — aceitável, default é últimos 30 dias).
 */
export function FiscalHubPage(): JSX.Element {
  const { active, setActive } = useTabParam(['emitidas', 'recorrentes'] as const, 'emitidas');

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-2">
        <div>
          <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">Notas Fiscais</h1>
          <p className="mt-1 text-[13px] text-muted">
            NFs emitidas via Asaas Notas e agendamentos de NF recorrente.
          </p>
        </div>
        <PageHelp id="fiscal" />
      </div>

      <PageTabs tabs={FISCAL_TABS} active={active} onChange={setActive} ariaLabel="Notas Fiscais" />

      <TabPanel id={active}>
        {active === 'emitidas' ? <FiscalIssuedTab /> : <FiscalRecurringTab />}
      </TabPanel>
    </div>
  );
}

function FiscalIssuedTab(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<FiscalInvoiceStatus | 'all'>('all');
  const [fromDate, setFromDate] = useState(addDaysLocal(todayLocal(), -30));
  const [toDate, setToDate] = useState(todayLocal());
  const [page, setPage] = useState(1);
  const [drawerInvoiceId, setDrawerInvoiceId] = useState<string | null>(null);
  const [standaloneOpen, setStandaloneOpen] = useState(false);
  const pageSize = 25;

  const filters = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      from_date: fromDate,
      to_date: toDate,
      page,
      pageSize,
    }),
    [statusFilter, fromDate, toDate, page],
  );

  const list = useFiscalInvoices(filters);

  function clearFilters(): void {
    setStatusFilter('all');
    setFromDate(addDaysLocal(todayLocal(), -30));
    setToDate(todayLocal());
    setPage(1);
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <PillButton
          solid
          icon={<FileText size={14} strokeWidth={2} />}
          onClick={() => setStandaloneOpen(true)}
        >
          Emitir NF avulsa
        </PillButton>
      </div>

      <StandaloneEmitModal open={standaloneOpen} onOpenChange={setStandaloneOpen} />

      {/* Filtros */}
      <div className="rounded-[14px] border border-line bg-panel p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as FiscalInvoiceStatus | 'all');
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="De">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                tabular
              />
            )}
          </Field>
          <Field label="Até">
            {({ inputId }) => (
              <Input
                id={inputId}
                type="date"
                value={toDate}
                min={fromDate}
                max={todayLocal()}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                tabular
              />
            )}
          </Field>
          <div className="flex items-end">
            <PillButton onClick={clearFilters} icon={<Filter size={14} strokeWidth={2} />}>
              Limpar filtros
            </PillButton>
          </div>
        </div>
      </div>

      {/* Tabela */}
      {list.isLoading ? (
        <SkeletonList />
      ) : list.isError ? (
        <div className="flex items-center gap-2 rounded-[14px] border border-bad/40 bg-bad/5 p-4 text-[13px] text-bad">
          <AlertCircle size={16} strokeWidth={2} />
          Falha ao carregar NFs. Tente recarregar a página.
        </div>
      ) : list.data && list.data.data.length > 0 ? (
        <FiscalInvoicesTable items={list.data.data} onRowClick={(id) => setDrawerInvoiceId(id)} />
      ) : (
        <EmptyState />
      )}

      {/* Pagination */}
      {list.data && list.data.pagination.total > pageSize && (
        <div className="flex items-center justify-between gap-2 text-[12px] text-muted">
          <span>
            Página {list.data.pagination.page} de {Math.ceil(list.data.pagination.total / pageSize)}{' '}
            · {list.data.pagination.total} NF
            {list.data.pagination.total === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <PillButton onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
              Anterior
            </PillButton>
            <PillButton
              onClick={() => setPage((p) => p + 1)}
              disabled={page * pageSize >= list.data.pagination.total}
            >
              Próxima
            </PillButton>
          </div>
        </div>
      )}

      {/* Drawer */}
      <FiscalInvoiceDrawer invoiceId={drawerInvoiceId} onClose={() => setDrawerInvoiceId(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabela
// ---------------------------------------------------------------------------

function FiscalInvoicesTable({
  items,
  onRowClick,
}: {
  items: FiscalInvoice[];
  onRowClick: (id: string) => void;
}): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      <table className="w-full text-[13px]">
        <thead className="border-b border-line bg-panel-2">
          <tr className="text-left text-[11px] uppercase tracking-wider text-muted-2">
            <th className="px-4 py-3 font-medium">NF</th>
            <th className="px-4 py-3 font-medium">Tomador</th>
            <th className="px-4 py-3 font-medium tnum">Valor</th>
            <th className="px-4 py-3 font-medium tnum">ISS</th>
            <th className="px-4 py-3 font-medium">Status</th>
            <th className="px-4 py-3 font-medium">Emitida</th>
            <th className="px-4 py-3" />
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => (
            <tr
              key={inv.id}
              className="cursor-pointer border-b border-line last:border-b-0 hover:bg-panel-2"
              onClick={() => onRowClick(inv.id)}
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <Receipt size={13} strokeWidth={1.8} className="text-muted" />
                  <span className="font-medium text-ink">
                    {inv.invoiceNumber ?? <span className="text-muted-3">—</span>}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                {inv.client?.name ? (
                  <div className="flex flex-col">
                    <span className="text-ink">{inv.client.name}</span>
                    <span className="tnum text-[11px] text-muted-2">
                      {inv.takerDocument ?? '—'}
                      {inv.takerDocumentWasOverride && (
                        <span className="ml-1 text-[10px] uppercase text-muted-3">(override)</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <span className="tnum text-muted">
                    {inv.takerDocument ?? '—'}
                    {inv.takerDocumentWasOverride && (
                      <span className="ml-1 text-[10px] uppercase text-muted-3">(override)</span>
                    )}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 tnum text-ink">R$ {inv.amount}</td>
              <td className="px-4 py-3 tnum text-muted">
                {inv.issAmount ? `R$ ${inv.issAmount}` : '—'}
              </td>
              <td className="px-4 py-3">
                <FiscalStatusBadge status={inv.status} />
              </td>
              <td className="px-4 py-3 text-muted-2">
                {inv.issuedAt ? formatBR(inv.issuedAt) : '—'}
              </td>
              <td className="px-4 py-3 text-right">
                {inv.externalId && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      void triggerFiscalDownload(
                        inv.id,
                        'pdf',
                        `NF-${inv.invoiceNumber ?? inv.id}.pdf`,
                      );
                    }}
                    title="Baixar PDF"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
                  >
                    <Download size={12} strokeWidth={1.8} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FiscalStatusBadge({ status }: { status: string }): JSX.Element {
  const tone = STATUS_TONE[status] ?? 'border-line bg-panel-3 text-muted';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-[6px] border px-2 py-[2px] text-[10px] font-medium uppercase tracking-wider',
        tone,
      )}
    >
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

function SkeletonList(): JSX.Element {
  return (
    <div className="space-y-2">
      {[...Array<number>(5)].map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-[10px] border border-line bg-panel-2" />
      ))}
    </div>
  );
}

function EmptyState(): JSX.Element {
  return (
    <RichEmptyState
      icon={Receipt}
      title="Nenhuma NFS-e por aqui"
      description="Notas Fiscais de Serviço aparecem aqui quando um pagamento é confirmado e o plano tem emissão automática ativa — ou quando você emite manual."
      bullets={[
        'Emissão automática: marque "auto_emit_nf" no plano',
        'Emissão manual: vá numa cobrança paga → botão "Emitir NF"',
        'Falhas vão pra DLQ pra reprocessar',
      ]}
      primaryAction={{ label: 'Ver planos', href: '/plans' }}
      secondaryAction={{ label: 'Cobranças pagas', href: '/charges?status=paid' }}
    />
  );
}

// ---------------------------------------------------------------------------
// Drawer
// ---------------------------------------------------------------------------

function FiscalInvoiceDrawer({
  invoiceId,
  onClose,
}: {
  invoiceId: string | null;
  onClose: () => void;
}): JSX.Element {
  const detail = useFiscalInvoice(invoiceId ?? undefined);
  const [cancelOpen, setCancelOpen] = useState(false);
  const open = Boolean(invoiceId);
  const inv = detail.data;

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
        <Dialog.Portal>
          <Dialog.Overlay
            className={cn(
              'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
            )}
          />
          <Dialog.Content
            className={cn(
              'fixed right-0 top-0 z-50 h-screen w-[min(560px,100vw)]',
              'border-l border-line bg-panel shadow-xl focus:outline-none',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
              'flex flex-col',
            )}
          >
            <header className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
              <div>
                <Dialog.Title className="flex items-center gap-2 text-[16px] font-semibold text-ink">
                  <Receipt size={16} strokeWidth={2} />
                  {inv?.invoiceNumber ?? 'NF sem número'}
                </Dialog.Title>
                {inv && <FiscalStatusBadge status={inv.status} />}
              </div>
              <Dialog.Close asChild>
                <button
                  type="button"
                  aria-label="Fechar"
                  className="flex h-7 w-7 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </Dialog.Close>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {detail.isLoading ? (
                <SkeletonList />
              ) : inv ? (
                <div className="space-y-6">
                  {/* Ações */}
                  {inv.externalId && (
                    <div className="flex flex-wrap gap-2">
                      <PillButton
                        onClick={() =>
                          void triggerFiscalDownload(
                            inv.id,
                            'pdf',
                            `NF-${inv.invoiceNumber ?? inv.id}.pdf`,
                          )
                        }
                        icon={<Download size={13} strokeWidth={2} />}
                      >
                        PDF
                      </PillButton>
                      <PillButton
                        onClick={() =>
                          void triggerFiscalDownload(
                            inv.id,
                            'xml',
                            `NF-${inv.invoiceNumber ?? inv.id}.xml`,
                          )
                        }
                        icon={<Download size={13} strokeWidth={2} />}
                      >
                        XML
                      </PillButton>
                      {inv.status === 'issued' && (
                        <PillButton danger onClick={() => setCancelOpen(true)}>
                          Cancelar NF
                        </PillButton>
                      )}
                    </div>
                  )}

                  {/* Tomador */}
                  <Section title="Tomador">
                    {inv.client?.name && <DataLine label="Nome" value={inv.client.name} />}
                    <DataLine
                      label="Documento"
                      value={inv.takerDocument ?? '—'}
                      tabular
                      suffix={inv.takerDocumentWasOverride ? '(override)' : undefined}
                    />
                  </Section>

                  {/* Discriminação */}
                  {inv.serviceDescription && (
                    <Section title="Discriminação">
                      <p className="whitespace-pre-wrap text-[13px] text-ink-2">
                        {inv.serviceDescription}
                      </p>
                    </Section>
                  )}

                  {/* Valores */}
                  <Section title="Valores">
                    <DataLine label="Valor bruto" value={`R$ ${inv.amount}`} tabular />
                    {inv.issRate && (
                      <DataLine label="Alíquota ISS" value={`${inv.issRate}%`} tabular />
                    )}
                    {inv.issAmount && (
                      <DataLine label="ISS retido" value={`R$ ${inv.issAmount}`} tabular />
                    )}
                    {inv.retencaoTotal && (
                      <DataLine
                        label="Retenções totais"
                        value={`R$ ${inv.retencaoTotal}`}
                        tabular
                      />
                    )}
                  </Section>

                  {/* Erros */}
                  {inv.errorMessage && (
                    <Section title="Erro">
                      <p className="rounded-[8px] border border-bad/30 bg-bad/5 p-3 text-[12px] text-bad">
                        {inv.errorMessage}
                      </p>
                    </Section>
                  )}

                  {/* Timeline */}
                  {inv.events.length > 0 && (
                    <Section title="Histórico">
                      <ol className="space-y-2">
                        {inv.events.map((e) => (
                          <li
                            key={e.id}
                            className="rounded-[8px] border border-line bg-panel-2 p-3"
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-[12px] font-medium text-ink">
                                {humanizeEvent(e.eventType)}
                              </span>
                              <span className="text-[11px] text-muted tnum">
                                {formatBRDateTime(e.occurredAt)}
                              </span>
                            </div>
                            {e.correlationId && (
                              <code className="mt-1 block font-mono text-[10px] text-muted-3">
                                {e.correlationId}
                              </code>
                            )}
                          </li>
                        ))}
                      </ol>
                    </Section>
                  )}
                </div>
              ) : (
                <div className="text-center text-[13px] text-muted">NF não encontrada.</div>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {inv && (
        <CancelFiscalInvoiceModal
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          fiscalInvoiceId={inv.id}
          invoiceNumber={inv.invoiceNumber}
          onSuccess={onClose}
        />
      )}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-3">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function DataLine({
  label,
  value,
  tabular,
  suffix,
}: {
  label: string;
  value: string;
  tabular?: boolean;
  suffix?: string;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-2 border-b border-line/60 pb-2 last:border-b-0">
      <span className="text-[11px] uppercase tracking-wider text-muted-3">{label}</span>
      <span className={cn('text-[13px] text-ink', tabular && 'tnum')}>
        {value}
        {suffix && <span className="ml-1 text-[11px] text-muted-2">{suffix}</span>}
      </span>
    </div>
  );
}

function humanizeEvent(type: string): string {
  const labels: Record<string, string> = {
    pending_data: 'Pré-requisitos não atendidos',
    provider_responded: 'Provider respondeu',
    provider_error: 'Erro do provider',
    manual_emit_succeeded: 'Emissão manual',
    webhook_received: 'Webhook Asaas',
    cancelled_requested: 'Cancelamento solicitado',
    cancel_provider_error: 'Erro ao cancelar',
    dlq_reprocessed: 'Reprocessada via DLQ',
    discarded: 'Descartada',
  };
  return labels[type] ?? type;
}

// Placeholder pra useFiscalInvoice retornar events corretamente
// (já está no use-fiscal.ts via fiscalInvoiceDetailSchema)
